import {Badge} from "@/components/ui/badge";
import {resolveTimezone} from "@/src/modules/core/lib";
import {useStoredCompany} from "@/src/modules/company/store";
import {computePermanentPermitCoverage} from "@/src/modules/permanent-parking/lib/compute-permanent-permit-coverage";
import {PermitCoverageStatusCluster} from "@/src/modules/permanent-parking/components/permit-coverage-status-cluster";
import type {PermanentParkingPermit} from "@/src/modules/permanent-parking/types";
import {formatVehicleMakeModelColorState} from "@/src/modules/vehicles/lib/format-vehicle-make-model-color-state";
import {cn} from "@/lib/utils";

type ResidentHomePermanentPermitsPreviewRowProps = {
  permit: PermanentParkingPermit;
  onSelect: (id: number) => void;
  className?: string;
  /** @deprecated Display uses API `status` / household embed on the permit row. */
  amenityRestricted?: boolean;
};

export function ResidentHomePermanentPermitsPreviewRow({
  permit,
  onSelect,
  className,
}: ResidentHomePermanentPermitsPreviewRowProps) {
  const company = useStoredCompany();
  const timeZone = resolveTimezone(company?.timezone);

  const permitNumber = permit.permit_number?.trim() || `Permit #${permit.id}`;
  const plate = permit.vehicle?.plate?.trim() || "—";
  const vehicleMeta = formatVehicleMakeModelColorState(
    permit.vehicle as Parameters<typeof formatVehicleMakeModelColorState>[0],
  );
  const coverage = computePermanentPermitCoverage(permit.start_date, permit.end_date, timeZone);

  return (
    <button
      type="button"
      onClick={() => onSelect(permit.id)}
      className={cn(
        "hover:bg-muted/50 flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="min-w-0 truncate text-sm font-medium tabular-nums">
          <span className="text-foreground">{plate}</span>
          {vehicleMeta ? (
            <span className="text-muted-foreground ml-1.5 text-xs font-normal">
              ({vehicleMeta})
            </span>
          ) : null}
        </p>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {coverage?.daysLabel ? (
            <Badge variant="outline" className="shrink-0 font-normal tabular-nums">
              {coverage.daysLabel}
            </Badge>
          ) : null}
          {coverage?.overallRangeLabel ? (
            <span className="text-muted-foreground min-w-0 truncate text-xs leading-snug tabular-nums">
              {coverage.overallRangeLabel}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <PermitCoverageStatusCluster
          permit={permit}
          timeZone={timeZone}
          badgeClassName="text-xs font-medium"
        />
        <Badge variant="outline" className="shrink-0 font-normal tabular-nums">
          {permitNumber}
        </Badge>
      </div>
    </button>
  );
}
